import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Film, Camera, Video, Shield, Heart, Hammer, Star, Users } from 'lucide-react';
import { heroSlides, services } from '../data/mock';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [featuredModels, setFeaturedModels] = useState([]);

  useEffect(() => {
    fetchFeaturedModels();
  }, []);

  const fetchFeaturedModels = async () => {
    try {
      const response = await axios.get(`${API}/models/featured/list`);
      setFeaturedModels(response.data || []);
    } catch (error) {
      console.error('Error fetching featured models:', error);
    }
  };

  return (
    <div className="bg-black text-white">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover"
          style={{
            backgroundImage: `linear-gradient(rgba(139, 0, 0, 0.2), rgba(0, 0, 0, 0.4)), url(${heroSlides[0].image})`,
            backgroundPosition: 'center top',
          }}
        />
        <div className="relative z-10 container mx-auto px-4 lg:px-8">
          <div className="max-w-3xl mx-auto text-center lg:text-left lg:mx-0">
            {/* Text content */}
            <h1 className="font-display text-5xl md:text-7xl lg:text-8xl font-bold text-white mb-6 uppercase tracking-tight drop-shadow-lg">
              EWS Productions
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-8 max-w-2xl mx-auto lg:mx-0 leading-relaxed drop-shadow-lg">
              Cinematic Studio Hire for Film, Fashion & Alternative Production
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-4">
              <Link 
                to="/studio-hire"
                className="cta-button inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-full font-mono text-sm tracking-widest uppercase transition-all"
              >
                View Studio <ArrowRight size={16} />
              </Link>
              <Link 
                to="/contact"
                className="cta-button inline-flex items-center gap-2 bg-transparent border-2 border-red-600 hover:bg-red-600/10 text-red-500 px-8 py-4 rounded-full font-mono text-sm tracking-widest uppercase transition-all"
              >
                Enquire Now
              </Link>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <Link 
                to="/model-signup"
                className="cta-button inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-full font-mono text-sm tracking-widest uppercase transition-all"
              >
                Join as Model <ArrowRight size={16} />
              </Link>
              <Link 
                to="/join-our-team"
                className="cta-button inline-flex items-center gap-2 bg-transparent border-2 border-red-600 hover:bg-red-600/10 text-red-500 px-8 py-4 rounded-full font-mono text-sm tracking-widest uppercase transition-all"
              >
                Join as Photographer
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20 lg:py-32 bg-zinc-950">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-display text-4xl md:text-5xl font-bold text-white mb-4 uppercase">
              Our Services
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Professional, discreet spaces for ambitious creative projects
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-8">
            {services.map((service, index) => {
              const icons = [Film, Hammer, Camera, Heart, Video];
              const IconComponent = icons[index];
              return (
                <div 
                  key={service.id}
                  className="group bg-zinc-900 rounded-lg p-8 hover:bg-zinc-800 transition-all duration-300 border border-red-900/20 hover:border-red-600/50"
                >
                  <div className="flex items-center justify-center w-16 h-16 rounded-full bg-red-600/10 mb-6 group-hover:bg-red-600/20 transition-colors">
                    <IconComponent className="text-red-500" size={28} />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-4">
                    {service.title}
                  </h3>
                  <p className="text-gray-400 mb-6 leading-relaxed text-sm">
                    {service.description}
                  </p>
                  <Link 
                    to={`/${service.title.toLowerCase().replace(/\s+/g, '-').replace('&', '').replace(/,/g, '')}`}
                    className="inline-flex items-center gap-2 text-red-500 hover:text-red-400 font-mono text-xs tracking-wider uppercase transition-colors"
                  >
                    Learn More <ArrowRight size={14} />
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Trust Section - Ethics */}
      <section className="py-20 lg:py-32 bg-black border-t border-red-900/20">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 rounded-full bg-red-600/10 flex items-center justify-center">
                <Shield className="text-red-500" size={36} />
              </div>
            </div>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-white mb-6 uppercase">
              Ethics, Consent & Safety
            </h2>
            <p className="text-gray-400 text-lg leading-relaxed mb-8">
              We maintain the highest standards of professionalism and respect. Every project operates under clear consent protocols, with confidentiality and performer safety as our top priorities.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
              <div className="bg-zinc-900 rounded-lg p-6 border border-red-900/20">
                <h3 className="text-xl font-semibold text-red-500 mb-3">Clear Consent Protocols</h3>
                <p className="text-gray-400">All participants provide informed consent with clear boundaries established before any production begins.</p>
              </div>
              <div className="bg-zinc-900 rounded-lg p-6 border border-red-900/20">
                <h3 className="text-xl font-semibold text-red-500 mb-3">Professional Conduct</h3>
                <p className="text-gray-400">We enforce strict professional standards and maintain a respectful environment at all times.</p>
              </div>
              <div className="bg-zinc-900 rounded-lg p-6 border border-red-900/20">
                <h3 className="text-xl font-semibold text-red-500 mb-3">Confidentiality</h3>
                <p className="text-gray-400">Complete discretion guaranteed. Your creative projects remain private and secure.</p>
              </div>
              <div className="bg-zinc-900 rounded-lg p-6 border border-red-900/20">
                <h3 className="text-xl font-semibold text-red-500 mb-3">Secure Booking</h3>
                <p className="text-gray-400">Private, controlled access with thorough vetting procedures for all bookings.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Models Section */}
      {featuredModels.length > 0 && (
        <section className="py-20 lg:py-32 bg-black border-t border-red-900/20">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="text-center mb-12">
              <div className="flex justify-center items-center gap-3 mb-4">
                <Star className="text-yellow-500" size={28} fill="currentColor" />
                <h2 className="font-display text-4xl md:text-5xl font-bold text-white uppercase">
                  Featured Models
                </h2>
              </div>
              <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                Discover our talented freelance models available for your next project
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {featuredModels.slice(0, 3).map((model) => (
                <Link 
                  key={model.id}
                  to={`/models/${model.id}`}
                  className="group relative bg-zinc-900 rounded-lg overflow-hidden border border-yellow-500/30 hover:border-yellow-400 transition-all duration-300"
                >
                  {/* Featured Badge */}
                  <div className="absolute top-3 left-3 z-10 flex items-center gap-1 bg-yellow-500 text-black px-3 py-1 rounded-full text-xs font-bold">
                    <Star size={12} fill="currentColor" /> Featured
                  </div>
                  
                  {/* Image */}
                  <div className="aspect-[3/4] overflow-hidden">
                    <img 
                      src={model.image_urls[0] ? `${BACKEND_URL}${model.image_urls[0]}` : 'https://via.placeholder.com/400x500?text=No+Image'}
                      alt={model.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  
                  {/* Info */}
                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-white mb-2 group-hover:text-yellow-400 transition-colors">
                      {model.name}
                    </h3>
                    {model.categories && model.categories.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {model.categories.slice(0, 2).map(cat => (
                          <span key={cat} className="text-xs bg-zinc-800 text-gray-400 px-2 py-1 rounded">
                            {cat}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </Link>
              ))}
            </div>

            <div className="text-center mt-12">
              <Link 
                to="/freelance-models"
                className="inline-flex items-center gap-2 bg-transparent border-2 border-red-600 hover:bg-red-600/10 text-red-500 px-8 py-4 rounded-full font-mono text-sm tracking-widest uppercase transition-all"
              >
                <Users size={18} /> View All Models <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 lg:py-32 bg-zinc-950">
        <div className="container mx-auto px-4 lg:px-8 text-center">
          <h2 className="font-display text-4xl md:text-5xl font-bold text-white mb-6 uppercase">
            Ready to Create?
          </h2>
          <p className="text-gray-400 text-lg mb-8 max-w-2xl mx-auto">
            Get in touch to discuss your project and book our studio
          </p>
          <Link 
            to="/contact"
            className="cta-button inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-full font-mono text-sm tracking-widest uppercase transition-all"
          >
            Contact Us <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;